import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { money, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const field = "w-full border border-neutral-200 px-3 py-3 text-sm focus:border-neutral-900 outline-none";
const STATUS_FR = { paid: "Payée", pending: "Paiement en attente", preparing: "En préparation", shipped: "Expédiée", delivered: "Livrée", cancelled: "Annulée", refunded: "Remboursée" };

export default function Account() {
  const { user, ready, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [addr, setAddr] = useState({ label: "Domicile", line1: "", city: "", zip: "", country: "France" });

  useEffect(() => {
    if (ready && !user) { nav("/connexion"); return; }
    if (user) {
      setProfile({ name: user.name || "", phone: user.phone || "" });
      api.get("/orders").then(({ data }) => setOrders(data.orders));
    }
  }, [user, ready, nav]);

  if (!user) return <div className="h-[50vh]" />;

  const saveProfile = async (e) => {
    e.preventDefault();
    try { await api.put("/auth/profile", profile); await refreshUser(); toast.success("Profil mis à jour"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const addAddress = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/addresses", { ...addr, default: false }); await refreshUser(); toast.success("Adresse ajoutée"); setAddr({ label: "Domicile", line1: "", city: "", zip: "", country: "France" }); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const delAddress = async (id) => { await api.delete(`/auth/addresses/${id}`); await refreshUser(); };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 py-14">
      <div className="flex items-center justify-between mb-8">
        <div>
          <span className="over text-[0.62rem] text-[#D4AF37]">Mon compte</span>
          <h1 className="font-display text-4xl font-light mt-2">Bonjour, {user.name}</h1>
        </div>
        <button onClick={logout} className="over text-[0.62rem] link-underline" data-testid="logout-button">Se déconnecter</button>
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-transparent border-b border-neutral-100 rounded-none w-full justify-start gap-6 p-0 h-auto">
          {[["orders", "Commandes"], ["profile", "Profil"], ["addresses", "Adresses"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="over text-[0.62rem] rounded-none border-b-2 border-transparent data-[state=active]:border-[#0A0A0A] data-[state=active]:shadow-none px-0 pb-3" data-testid={`account-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="orders" className="pt-8">
          {orders.length === 0 ? <p className="text-neutral-500 text-sm">Aucune commande pour le moment.</p> : (
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="border border-neutral-100 p-5" data-testid={`order-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs text-neutral-400">#{o.id.slice(0, 8)}</span>
                      <p className="text-sm mt-1">{new Date(o.created_at).toLocaleDateString("fr-FR")} · {o.items.length} article(s)</p>
                    </div>
                    <div className="text-right">
                      <span className="over text-[0.58rem] bg-neutral-100 px-2 py-1">{STATUS_FR[o.status] || o.status}</span>
                      <p className="text-sm mt-1">{money(o.total)}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    {o.items.map((it, i) => <img key={i} src={it.image} alt="" className="w-12 h-14 object-cover bg-neutral-100" />)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="pt-8">
          <form onSubmit={saveProfile} className="max-w-md space-y-4">
            <input placeholder="Nom" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={field} data-testid="profile-name" />
            <input placeholder="Téléphone" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className={field} />
            <input disabled value={user.email} className={`${field} bg-neutral-50 text-neutral-400`} />
            <button className="btn btn-dark" data-testid="profile-save">Enregistrer</button>
          </form>
        </TabsContent>

        <TabsContent value="addresses" className="pt-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3">
              {(user.addresses || []).map((a) => (
                <div key={a.id} className="border border-neutral-100 p-4 flex justify-between">
                  <div className="text-sm"><p className="font-medium">{a.label}</p><p className="text-neutral-500">{a.line1}, {a.zip} {a.city}, {a.country}</p></div>
                  <button onClick={() => delAddress(a.id)} className="text-xs text-[#E0115F]">Supprimer</button>
                </div>
              ))}
              {(!user.addresses || !user.addresses.length) && <p className="text-sm text-neutral-500">Aucune adresse enregistrée.</p>}
            </div>
            <form onSubmit={addAddress} className="space-y-3">
              <span className="over text-[0.62rem]">Ajouter une adresse</span>
              <input placeholder="Libellé" value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} className={field} />
              <input required placeholder="Adresse" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className={field} />
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Code postal" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} className={field} />
                <input required placeholder="Ville" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className={field} />
              </div>
              <input placeholder="Pays" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} className={field} />
              <button className="btn btn-outline" data-testid="address-add">Ajouter</button>
            </form>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
